import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Gift, 
  Star, 
  Trophy,
  Zap,
  Crown,
  Target,
  ArrowLeft,
  Loader2,
  CheckCircle,
  Lock,
  Sparkles,
  Calendar,
  Users,
  Heart
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Loyalty tiers configuration
const LOYALTY_TIERS = [
  { 
    name: 'Bronze', 
    minPoints: 0, 
    maxPoints: 499,
    color: 'from-amber-600 to-amber-700',
    icon: Star,
    benefits: ['5% de réduction sur les consultations', 'Accès prioritaire aux créneaux']
  },
  { 
    name: 'Argent', 
    minPoints: 500, 
    maxPoints: 1499,
    color: 'from-slate-400 to-slate-500',
    icon: Zap,
    benefits: ['10% de réduction', 'Rappels SMS gratuits', 'Support prioritaire']
  },
  { 
    name: 'Or', 
    minPoints: 1500, 
    maxPoints: 4999,
    color: 'from-yellow-500 to-amber-500',
    icon: Trophy,
    benefits: ['15% de réduction', 'Téléconsultation offerte/mois', 'Conciergerie médicale']
  },
  { 
    name: 'Platine', 
    minPoints: 5000, 
    maxPoints: Infinity,
    color: 'from-purple-500 to-indigo-600',
    icon: Crown,
    benefits: ['20% de réduction', 'Accès VIP', 'Gestionnaire de compte dédié', 'Avantages partenaires']
  }
];

// Rewards catalog
const REWARDS = [
  { id: 'discount_10', name: '10€ de réduction', points: 200, icon: Gift, category: 'discount' },
  { id: 'discount_25', name: '25€ de réduction', points: 450, icon: Gift, category: 'discount' },
  { id: 'teleconsult_free', name: 'Téléconsultation gratuite', points: 300, icon: Calendar, category: 'service' },
  { id: 'priority_booking', name: 'Réservation prioritaire (1 mois)', points: 150, icon: Zap, category: 'service' },
  { id: 'family_discount', name: 'Réduction famille (-15%)', points: 500, icon: Users, category: 'discount' },
];

// Achievements/Badges
const ACHIEVEMENTS = [
  { id: 'first_booking', name: 'Premier pas', description: 'Premier rendez-vous réservé', icon: Star, points: 50 },
  { id: 'loyal_5', name: 'Fidèle', description: '5 rendez-vous complétés', icon: Heart, points: 100 },
  { id: 'loyal_10', name: 'Super fidèle', description: '10 rendez-vous complétés', icon: Trophy, points: 200 },
  { id: 'referral_1', name: 'Ambassadeur', description: 'Premier parrainage réussi', icon: Users, points: 150 },
  { id: 'referral_5', name: 'Super ambassadeur', description: '5 parrainages réussis', icon: Crown, points: 500 },
  { id: 'reviewer', name: 'Critique', description: 'Premier avis laissé', icon: Star, points: 30 },
  { id: 'early_bird', name: 'Lève-tôt', description: 'RDV avant 9h', icon: Zap, points: 20 },
  { id: 'teleconsult_fan', name: 'Digital native', description: '3 téléconsultations', icon: Sparkles, points: 75 },
];

export const LoyaltyProgram = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loyaltyData, setLoyaltyData] = useState({
    points: 0,
    total_points_earned: 0,
    tier: 'Bronze',
    achievements: [],
    history: [],
    redeemed_rewards: []
  });
  const [redeeming, setRedeeming] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchLoyaltyData();
  }, [token]);

  const fetchLoyaltyData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/loyalty/status`, { headers });
      setLoyaltyData(response.data);
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
    } finally {
      setLoading(false);
    }
  };

  const redeemReward = async (rewardId) => {
    const reward = REWARDS.find(r => r.id === rewardId);
    if (!reward || loyaltyData.points < reward.points) {
      toast.error('Points insuffisants');
      return;
    }

    setRedeeming(rewardId);
    try {
      await axios.post(
        `${API_URL}/api/loyalty/redeem`,
        { reward_id: rewardId },
        { headers }
      );
      toast.success(`${reward.name} échangé avec succès !`);
      fetchLoyaltyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'échange');
    } finally {
      setRedeeming(null);
    }
  };

  const getCurrentTier = () => {
    return LOYALTY_TIERS.find(t => 
      loyaltyData.points >= t.minPoints && loyaltyData.points <= t.maxPoints
    ) || LOYALTY_TIERS[0];
  };

  const getNextTier = () => {
    const currentIndex = LOYALTY_TIERS.findIndex(t => t.name === getCurrentTier().name);
    return LOYALTY_TIERS[currentIndex + 1] || null;
  };

  const getProgressToNextTier = () => {
    const current = getCurrentTier();
    const next = getNextTier();
    if (!next) return 100;
    
    const pointsInTier = loyaltyData.points - current.minPoints;
    const tierRange = next.minPoints - current.minPoints;
    return Math.min(100, (pointsInTier / tierRange) * 100);
  };

  const currentTier = getCurrentTier();
  const nextTier = getNextTier();
  const TierIcon = currentTier.icon;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="loyalty-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour au tableau de bord
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Gift className="w-8 h-8 text-primary" />
            Programme de Fidélité
          </h1>
          <p className="text-slate-500 mt-1">
            Gagnez des points et débloquez des récompenses exclusives
          </p>
        </div>

        {/* Current Status Card */}
        <Card className={`mb-8 overflow-hidden bg-gradient-to-r ${currentTier.color} text-white`}>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <TierIcon className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-white/80 text-sm">Votre niveau</p>
                  <h2 className="text-3xl font-bold">{currentTier.name}</h2>
                  <p className="text-white/90 text-lg font-semibold mt-1">
                    {loyaltyData.points.toLocaleString()} points
                  </p>
                </div>
              </div>
              
              {nextTier && (
                <div className="flex-1 max-w-sm">
                  <div className="flex justify-between text-sm text-white/80 mb-2">
                    <span>Prochain niveau: {nextTier.name}</span>
                    <span>{nextTier.minPoints - loyaltyData.points} points restants</span>
                  </div>
                  <Progress value={getProgressToNextTier()} className="h-3 bg-white/20" />
                </div>
              )}
            </div>
            
            {/* Current tier benefits */}
            <div className="mt-6 pt-6 border-t border-white/20">
              <p className="text-sm text-white/80 mb-3">Vos avantages actuels :</p>
              <div className="flex flex-wrap gap-2">
                {currentTier.benefits.map((benefit, i) => (
                  <Badge key={i} className="bg-white/20 text-white hover:bg-white/30">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {benefit}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tiers Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {LOYALTY_TIERS.map((tier) => {
            const isCurrentOrPast = loyaltyData.points >= tier.minPoints;
            const Icon = tier.icon;
            return (
              <Card 
                key={tier.name}
                className={`text-center ${isCurrentOrPast ? '' : 'opacity-50'}`}
              >
                <CardContent className="p-4">
                  <div className={`w-12 h-12 mx-auto rounded-full bg-gradient-to-r ${tier.color} flex items-center justify-center mb-2`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-bold">{tier.name}</p>
                  <p className="text-xs text-slate-500">{tier.minPoints}+ pts</p>
                  {isCurrentOrPast && <CheckCircle className="w-4 h-4 text-green-500 mx-auto mt-2" />}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Rewards Catalog */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-primary" />
              Récompenses disponibles
            </CardTitle>
            <CardDescription>
              Échangez vos points contre des avantages exclusifs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {REWARDS.map((reward) => {
                const canRedeem = loyaltyData.points >= reward.points;
                const Icon = reward.icon;
                return (
                  <div 
                    key={reward.id}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      canRedeem 
                        ? 'border-primary/20 bg-primary/5 hover:border-primary/40' 
                        : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        canRedeem ? 'bg-primary/10' : 'bg-slate-200'
                      }`}>
                        <Icon className={`w-5 h-5 ${canRedeem ? 'text-primary' : 'text-slate-400'}`} />
                      </div>
                      <Badge variant={canRedeem ? "default" : "secondary"}>
                        {reward.points} pts
                      </Badge>
                    </div>
                    <p className={`font-medium ${canRedeem ? 'text-slate-900' : 'text-slate-500'}`}>
                      {reward.name}
                    </p>
                    <Button 
                      size="sm" 
                      className="w-full mt-3"
                      variant={canRedeem ? "default" : "outline"}
                      disabled={!canRedeem || redeeming === reward.id}
                      onClick={() => redeemReward(reward.id)}
                    >
                      {redeeming === reward.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : canRedeem ? (
                        'Échanger'
                      ) : (
                        <>
                          <Lock className="w-3 h-3 mr-1" />
                          {reward.points - loyaltyData.points} pts manquants
                        </>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Achievements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Badges & Accomplissements
            </CardTitle>
            <CardDescription>
              Débloquez des badges pour gagner des points bonus
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {ACHIEVEMENTS.map((achievement) => {
                const isUnlocked = loyaltyData.achievements?.includes(achievement.id);
                const Icon = achievement.icon;
                return (
                  <div 
                    key={achievement.id}
                    className={`p-4 rounded-lg text-center transition-all ${
                      isUnlocked 
                        ? 'bg-amber-50 border-2 border-amber-200' 
                        : 'bg-slate-50 border-2 border-slate-200 opacity-60'
                    }`}
                  >
                    <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${
                      isUnlocked ? 'bg-amber-100' : 'bg-slate-200'
                    }`}>
                      <Icon className={`w-6 h-6 ${isUnlocked ? 'text-amber-600' : 'text-slate-400'}`} />
                    </div>
                    <p className={`font-medium text-sm ${isUnlocked ? 'text-amber-900' : 'text-slate-500'}`}>
                      {achievement.name}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {achievement.description}
                    </p>
                    <Badge 
                      variant={isUnlocked ? "default" : "secondary"} 
                      className={`mt-2 ${isUnlocked ? 'bg-amber-500' : ''}`}
                    >
                      +{achievement.points} pts
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* How to earn points */}
        <Card className="mt-8 border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <h3 className="font-bold text-blue-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Comment gagner des points ?
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>Réserver un RDV : <strong>+10 pts</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>Compléter un RDV : <strong>+20 pts</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>Laisser un avis : <strong>+15 pts</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>Parrainer un ami : <strong>+100 pts</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>Téléconsultation : <strong>+25 pts</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>Compléter son profil : <strong>+50 pts</strong></span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoyaltyProgram;
